import * as React from 'react'
import styled from 'styled-components'

import * as copier from '@owl-nest/copier'
import { t } from '@owl-nest/localize'
import * as plume from '@ulule/owl-kit-components/next'

import * as UFE from './UFE'

export function ProjectWidgetPreview({ url }: { url?: string }): React.ReactElement {
  const widgetUrl = url ?? UFE.WIDGET_URL

  if (widgetUrl === undefined) {
    return <></>
  }

  const { canCopy, copy, copyStatus } = copier.useCopy(3000)

  return (
    <Wrapper>
      <div>
        <plume.styles.heading.XXXS>
          {t('Add a preview of the project by copying the following code:')}
        </plume.styles.heading.XXXS>
        <div>
          <CodePreview
            disabled
            name="widget-code-preview"
            onChange={() => {
              // no-op
            }}
            resizable={false}
            rows={6}
            value={`<iframe frameborder="0" height="300px" width="310px" src="${widgetUrl}" scrolling="no"></iframe>`}
          />

          {canCopy && copyStatus && (
            <plume.styles.tooltip.BlockAnchor>
              <plume.Tooltip
                fitToContent
                open={copyStatus !== 'idle'}
                position={plume.utils.isMobile() ? 'right' : 'bottom'}
              >
                {copyStatus === 'succeeded' ? (
                  <>
                    <plume.styles.copy.S.regular>{t('Code copied to your clipboard.')}</plume.styles.copy.S.regular>
                  </>
                ) : (
                  <>
                    <plume.styles.copy.S.regular>
                      {t("Sorry, we couldn't copy the code. Please try again!")}
                    </plume.styles.copy.S.regular>
                  </>
                )}
              </plume.Tooltip>

              <plume.LinkAsButton
                withIcon
                tinted
                onClick={() => {
                  copy(
                    `<iframe frameborder="0" height="300px" width="310px" src="${widgetUrl}" scrolling="no"></iframe>`,
                  )
                }}
              >
                <plume.glyphs.stroke.Link size={20} />
                {t('Copy code')}
              </plume.LinkAsButton>
            </plume.styles.tooltip.BlockAnchor>
          )}
        </div>
      </div>

      <WidgetPreviewWrapper>
        <SpinnerContainer>
          <plume.Spinner />
        </SpinnerContainer>
        <iframe frameBorder="0" height="300px" scrolling="no" src={widgetUrl} width="310px" title="Widget"></iframe>
      </WidgetPreviewWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  ${plume.styles.heading.XXXS} {
    margin-bottom: 17px;
  }

  ${plume.LinkAsButton} {
    ${plume.glyphs.stroke.Link} {
      color: ${({ theme }) => theme?.colors?.primary ?? plume.COLORS.PRIMARY_BLUE_500};
    }
  }

  ${plume.LinkAsButton}:hover {
    ${plume.glyphs.stroke.Link} {
      color: ${({ theme }) => theme?.colors?.primary ?? plume.COLORS.PRIMARY_BLUE_700};
    }
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    border: 1px solid ${plume.COLORS.PRIMARY_SAND_200};
    display: flex;
    padding: 24px;

    ${plume.styles.heading.XXXS} {
      max-width: 290px;
      margin-bottom: 17px;
    }
  }
`

const CodePreview = styled(plume.TextAreaField)`
  color: ${plume.COLORS.TEXT_TERTIARY};
  opacity: 1;
  padding: 0;
  resize: none;
  width: auto;
`

const SpinnerContainer = styled.span`
  position: absolute;
  top: 60%;
  z-index: ${plume.ZINDEX.NEUTRAL};
`

const WidgetPreviewWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  margin-top: 10px;

  iframe {
    position: relative;
    z-index: ${plume.ZINDEX.ABOVE};
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-top: 0px;
    margin-left: 26px;
  }
`
